import React, { FC, useMemo } from 'react';
import { graphql } from 'react-relay';
import { useLazyLoadQuery } from 'react-relay/hooks';
import { AdvancedSelect } from '../../../../widgets/select';
import { T } from 'src/shared/helpers';
import { ProductProvider } from './__generated__/importCatalogListQuery.graphql';
import { searchCategoryQuery } from './__generated__/searchCategoryQuery.graphql';

interface SearchCategoryProps {
    provider: ProductProvider;
    value: string | null;
    onChange: (id: string | null) => void;
}

type OptionType = {
    readonly label: string;
    readonly value: string;
};

type CategoryType = {
    readonly label: string;
    readonly options: OptionType[];
};

const SearchCategory: FC<SearchCategoryProps> = ({ provider, value, onChange }) => {
    const query = useLazyLoadQuery<searchCategoryQuery>(
        graphql`
            query searchCategoryQuery($provider: ProductProvider!) {
                providerCategories(provider: $provider) {
                    macroCategoryID
                    name
                    categories {
                        categoryID
                        name
                    }
                }
            }
        `,
        {
            provider
        }
    );

    // Build groups and items structure
    const options = useMemo(
        () =>
            query.providerCategories?.map((x) => {
                if (x.categories.length > 0) {
                    return {
                        label: x.name,
                        options: [
                            {
                                label: 'All',
                                value: 'macro_' + x.name
                            },
                            ...x.categories.map((j) => {
                                return {
                                    label: j.name,
                                    value: 'c_' + j.name
                                };
                            })
                        ]
                    };
                } else {
                    return {
                        label: x.name,
                        options: [
                            {
                                label: 'All',
                                value: 'm_' + x.name
                            }
                        ]
                    };
                }
            }) as CategoryType[],
        [query.providerCategories]
    );

    // Flat list of all options, used for making the search of the selected value simple with a find
    const allOptions = useMemo(() => options?.reduce<OptionType[]>((prev, curr) => (prev = prev.concat(curr.options)), []), [options]);

    return (
        <AdvancedSelect
            isClearable
            options={options}
            value={allOptions?.find((x) => x.value == value)}
            placeholder={T._('Filter by category...', 'Admin')}
            onChange={(option) => {
                onChange(option ? option.value : null);
            }}
        />
    );
};

export default SearchCategory;
